/**
 * Pagination component for navigating through paginated data.
 */

import { clsx } from 'clsx';

interface PaginationProps {
  /** Current page number (1-indexed) */
  currentPage: number;
  /** Whether there is a next page available */
  hasNextPage: boolean;
  /** Whether there is a previous page available */
  hasPrevPage: boolean;
  /** Callback for going to next page */
  onNextPage: () => void;
  /** Callback for going to previous page */
  onPrevPage: () => void;
  /** Callback for going to first page */
  onFirstPage?: () => void;
  /** Number of items on current page */
  pageItemCount?: number;
  /** Page size */
  pageSize?: number;
  /** Additional CSS classes */
  className?: string;
}

export function Pagination({
  currentPage,
  hasNextPage,
  hasPrevPage,
  onNextPage,
  onPrevPage,
  onFirstPage,
  pageItemCount,
  pageSize,
  className,
}: PaginationProps) {
  return (
    <div
      className={clsx(
        'flex items-center justify-between border-t border-dark-700 px-4 py-3',
        className
      )}
    >
      {/* Page info */}
      <div className="text-sm text-dark-400">
        Page {currentPage}
        {pageItemCount !== undefined && pageSize !== undefined && (
          <span className="ml-2 text-dark-500">
            ({pageItemCount} item{pageItemCount !== 1 ? 's' : ''})
          </span>
        )}
      </div>

      {/* Navigation buttons */}
      <div className="flex items-center gap-2">
        {onFirstPage && hasPrevPage && (
          <button
            onClick={onFirstPage}
            disabled={!hasPrevPage}
            className={clsx(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              hasPrevPage
                ? 'text-dark-200 hover:bg-dark-700 hover:text-dark-100'
                : 'cursor-not-allowed text-dark-600'
            )}
            aria-label="Go to first page"
          >
            First
          </button>
        )}
        <button
          onClick={onPrevPage}
          disabled={!hasPrevPage}
          className={clsx(
            'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
            hasPrevPage
              ? 'text-dark-200 hover:bg-dark-700 hover:text-dark-100'
              : 'cursor-not-allowed text-dark-600'
          )}
          aria-label="Go to previous page"
        >
          Previous
        </button>
        <span className="rounded-md bg-dark-800 px-3 py-1.5 text-sm font-medium text-dark-200">
          {currentPage}
        </span>
        <button
          onClick={onNextPage}
          disabled={!hasNextPage}
          className={clsx(
            'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
            hasNextPage
              ? 'text-dark-200 hover:bg-dark-700 hover:text-dark-100'
              : 'cursor-not-allowed text-dark-600'
          )}
          aria-label="Go to next page"
        >
          Next
        </button>
      </div>
    </div>
  );
}
